import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SendRounded from '@mui/icons-material/SendRounded';
import CircularProgress from '@mui/material/CircularProgress';
import { isAfter, isBefore, subSeconds, addMinutes } from 'date-fns';
import T from 'i18n';
import type { NoteTag, TranscriptHighlight } from '../types';
import { formatNoteTimestamp } from '../utils/formatters';

export type CommentsFilter = {
  from: Date | null;
  to: Date | null;
};

type CommentsPanelProps = {
  notes: TranscriptHighlight[];
  isScrolling?: boolean;
  onScroll?: React.UIEventHandler<HTMLDivElement>;
  onNoteClick: (note: TranscriptHighlight) => void;
  onSubmitComment?: (highlightId: string, commentText: string) => Promise<boolean>;
  searchQuery?: string;
  commentsFilter?: CommentsFilter;
};

type CommentEntry = {
  id: string;
  authorName?: string;
  text: string;
  timestamp?: string | number | Date;
};

type SelectionEntry = {
  id: string;
  text: string;
};

const COLLAPSED_SELECTION_COUNT = 2;
const COLLAPSED_COMMENT_COUNT = 1;

const TagDropdownBadge: React.FC<{ tags: NoteTag[] }> = ({ tags }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <span className="real-time__tag-dropdown-wrapper" ref={wrapperRef}>
      <button
        type="button"
        className={classnames(
          'real-time__comments-type-badge real-time__comments-type-badge--tag',
          {
            'is-open': open,
          },
        )}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
      >
        {tags.length > 1 ? 'TAGS' : 'TAG'}
        <KeyboardArrowDownIcon
          style={{
            fontSize: 14,
            transform: open ? 'rotate(180deg)' : undefined,
            transition: 'transform 0.2s',
          }}
        />
      </button>
      {open && (
        <div
          className="real-time__tag-dropdown-menu"
          onClick={(e) => e.stopPropagation()}
          role="presentation"
        >
          {tags.map((tag) => (
            <span key={tag.id} className="real-time__tag-dropdown-item">
              {tag.name}
            </span>
          ))}
        </div>
      )}
    </span>
  );
};

const formatHiddenCountLabel = (count: number, singular: string, plural: string) =>
  `Show ${count} more ${count === 1 ? singular : plural}`;

const getCommentEntries = (note: TranscriptHighlight): CommentEntry[] => {
  const entries: CommentEntry[] = [];
  const noteText = note.note?.trim() ?? '';

  if (noteText) {
    entries.push({
      id: `${note.highlightId}-note`,
      authorName: note.createdByName,
      text: noteText,
      timestamp: note.createdAt,
    });
  }

  (note.comments ?? []).forEach((comment, index) => {
    const commentText = comment.commentText?.trim() ?? '';
    if (!commentText) return;

    entries.push({
      id: comment.commentId || `${note.highlightId}-comment-${index}`,
      authorName: comment.createdBy?.name,
      text: commentText,
      timestamp: comment.createdAtUtc,
    });
  });

  return entries;
};

const getSelectionEntries = (note: TranscriptHighlight): SelectionEntry[] => {
  const selections =
    note.selections && note.selections.length > 0
      ? note.selections
      : [
          {
            lineId: note.lineId,
            text: note.text,
          },
        ];

  return selections
    .map((selection, index) => ({
      id: `${note.highlightId}-selection-${selection.lineId || index}`,
      text: selection.text?.trim() ?? '',
    }))
    .filter((selection) => selection.text.length > 0);
};

const getPageLineChips = (note: TranscriptHighlight): string[] => {
  const selections =
    note.selections && note.selections.length > 0
      ? note.selections
      : [
          {
            lineId: note.lineId,
            lineNumberAtCreate: note.lineNumberAtCreate,
            pageNumber: note.pageNumber,
          },
        ];

  // Group lines by page
  const pageMap = new Map<number, number[]>();
  const orphanLines: number[] = [];

  for (const s of selections) {
    const line = s.lineNumberAtCreate;
    if (s.pageNumber != null) {
      const lines = pageMap.get(s.pageNumber) ?? [];
      if (line != null) lines.push(line);
      pageMap.set(s.pageNumber, lines);
    } else if (line != null) {
      orphanLines.push(line);
    }
  }

  const chips: string[] = [];

  // Sort pages and build a chip per page
  const sortedPages = [...pageMap.keys()].sort((a, b) => a - b);
  for (const page of sortedPages) {
    const lines = pageMap.get(page)!;
    if (lines.length === 0) {
      chips.push(`Page ${page}`);
    } else {
      const minLine = Math.min(...lines);
      const maxLine = Math.max(...lines);
      const linePart = minLine === maxLine ? `Line ${minLine}` : `Lines ${minLine}\u2013${maxLine}`;
      chips.push(`Page ${page} \u00b7 ${linePart}`);
    }
  }

  // Lines without a page (fallback)
  if (orphanLines.length > 0 && sortedPages.length === 0) {
    const minLine = Math.min(...orphanLines);
    const maxLine = Math.max(...orphanLines);
    chips.push(minLine === maxLine ? `Line ${minLine}` : `Lines ${minLine}\u2013${maxLine}`);
  }

  return chips;
};

const matchesSearchQuery = (note: TranscriptHighlight, query: string): boolean => {
  const lowerQuery = query.toLowerCase();

  // Search note text
  if (note.note && note.note.toLowerCase().includes(lowerQuery)) return true;

  // Search selected text in selections array
  if (note.selections?.length) {
    if (note.selections.some((s) => s.text && s.text.toLowerCase().includes(lowerQuery)))
      return true;
  } else if (note.text && note.text.toLowerCase().includes(lowerQuery)) {
    return true;
  }

  // Search comment text in comments array
  if (note.comments?.length) {
    if (
      note.comments.some((c) => c.commentText && c.commentText.toLowerCase().includes(lowerQuery))
    )
      return true;
  }

  return false;
};

const isDateInRange = (date: Date, filter: CommentsFilter): boolean => {
  if (Number.isNaN(date.getTime())) return false;
  if (filter.from && !isAfter(date, subSeconds(filter.from, 1))) return false;
  if (filter.to && !isBefore(date, addMinutes(filter.to, 1))) return false;
  return true;
};

const matchesTimeFilter = (note: TranscriptHighlight, filter: CommentsFilter): boolean => {
  // Check the note's own creation time
  const noteDate = new Date(note.createdAt);
  if (isDateInRange(noteDate, filter)) return true;

  // Check individual comment timestamps
  if (note.comments?.length) {
    for (const comment of note.comments) {
      if (comment.createdAtUtc) {
        const commentDate = new Date(comment.createdAtUtc);
        if (isDateInRange(commentDate, filter)) return true;
      }
    }
  }

  return false;
};

const CommentsPanel: React.FC<CommentsPanelProps> = ({
  notes,
  isScrolling = false,
  onScroll,
  onNoteClick,
  onSubmitComment,
  searchQuery = '',
  commentsFilter,
}) => {
  const [expandedSelectionIds, setExpandedSelectionIds] = useState<Set<string>>(() => new Set());
  const [expandedCommentIds, setExpandedCommentIds] = useState<Set<string>>(() => new Set());
  const [replyTexts, setReplyTexts] = useState<Record<string, string>>({});
  const [replyOpenIds, setReplyOpenIds] = useState<Set<string>>(() => new Set());
  const [submittingReplyIds, setSubmittingReplyIds] = useState<Set<string>>(() => new Set());

  const trimmedQuery = searchQuery.trim();

  const commentNotes = useMemo(
    () =>
      [...notes]
        .sort((left, right) => right.createdAt - left.createdAt)
        .map((note) => ({
          note,
          commentEntries: getCommentEntries(note),
          selectionEntries: getSelectionEntries(note),
        }))
        .filter(
          ({ note, commentEntries, selectionEntries }) =>
            note.isCommentsVisible !== false &&
            (commentEntries.length > 0 || selectionEntries.length > 0) &&
            (trimmedQuery === '' || matchesSearchQuery(note, trimmedQuery)) &&
            (!commentsFilter || matchesTimeFilter(note, commentsFilter)),
        ),
    [notes, trimmedQuery, commentsFilter],
  );

  const toggleExpandedSelection = useCallback((highlightId: string) => {
    setExpandedSelectionIds((prev) => {
      const next = new Set(prev);
      if (next.has(highlightId)) next.delete(highlightId);
      else next.add(highlightId);
      return next;
    });
  }, []);

  const toggleExpandedComments = useCallback((highlightId: string) => {
    setExpandedCommentIds((prev) => {
      const next = new Set(prev);
      if (next.has(highlightId)) next.delete(highlightId);
      else next.add(highlightId);
      return next;
    });
  }, []);

  const toggleReplyOpen = useCallback((highlightId: string) => {
    setReplyOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(highlightId)) next.delete(highlightId);
      else next.add(highlightId);
      return next;
    });
  }, []);

  const handleReplyTextChange = useCallback((highlightId: string, text: string) => {
    setReplyTexts((prev) => ({ ...prev, [highlightId]: text }));
  }, []);

  const handleSubmitReply = useCallback(
    async (highlightId: string) => {
      const text = (replyTexts[highlightId] ?? '').trim();
      if (!text || !onSubmitComment || submittingReplyIds.has(highlightId)) return;
      setSubmittingReplyIds((prev) => {
        const next = new Set(prev);
        next.add(highlightId);
        return next;
      });
      try {
        const success = await onSubmitComment(highlightId, text);
        if (success) {
          setReplyTexts((prev) => ({ ...prev, [highlightId]: '' }));
          setReplyOpenIds((prev) => {
            const next = new Set(prev);
            next.delete(highlightId);
            return next;
          });
          setExpandedCommentIds((prev) => {
            const next = new Set(prev);
            next.add(highlightId);
            return next;
          });
        }
      } finally {
        setSubmittingReplyIds((prev) => {
          const next = new Set(prev);
          next.delete(highlightId);
          return next;
        });
      }
    },
    [replyTexts, onSubmitComment, submittingReplyIds],
  );

  return (
    <div
      className={classnames('real-time__docs-list', 'real-time__comments-list', {
        'is-scrolling': isScrolling,
      })}
      role="list"
      onScroll={onScroll}
    >
      {commentNotes.length === 0 ? (
        <div className="real-time__docs-empty">
          {T.translate('case.realTimeCommentsEmpty', { defaultValue: 'No comments yet.' })}
        </div>
      ) : (
        commentNotes.map(({ note, commentEntries, selectionEntries }) => {
          const isSelectionExpanded = expandedSelectionIds.has(note.highlightId);
          const isCommentsExpanded = expandedCommentIds.has(note.highlightId);
          const visibleSelectionEntries = isSelectionExpanded
            ? selectionEntries
            : selectionEntries.slice(0, COLLAPSED_SELECTION_COUNT);
          const visibleCommentEntries = isCommentsExpanded
            ? commentEntries
            : commentEntries.slice(0, COLLAPSED_COMMENT_COUNT);
          const hiddenSelectionCount = selectionEntries.length - visibleSelectionEntries.length;
          const hiddenCommentCount = commentEntries.length - visibleCommentEntries.length;
          const canExpandSelections = selectionEntries.length > COLLAPSED_SELECTION_COUNT;
          const canExpandComments = commentEntries.length > COLLAPSED_COMMENT_COUNT;

          return (
            <div
              key={note.highlightId}
              role="button"
              tabIndex={0}
              className={classnames('real-time__comments-thread', {
                'is-expanded': isSelectionExpanded || isCommentsExpanded,
              })}
              onClick={() => onNoteClick(note)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                onNoteClick(note);
              }}
              aria-label={`Open note for ${note.createdByName || 'selected text'}`}
            >
              <div className="real-time__comments-thread-body">
                {selectionEntries.length > 0 && (
                  <div className="real-time__comments-section">
                    <div className="real-time__comments-section-card real-time__comments-section-card--selection">
                      <span className="real-time__comments-section-title">
                        {T.translate('case.realTimeSelectedText', {
                          defaultValue: 'Selected Transcript',
                        })}
                        {note.tags && note.tags.length > 0 ? (
                          <TagDropdownBadge tags={note.tags} />
                        ) : note.note?.trim() ? (
                          <span className="real-time__comments-type-badge real-time__comments-type-badge--note">
                            NOTE
                          </span>
                        ) : (
                          <span className="real-time__comments-type-badge real-time__comments-type-badge--highlight">
                            HIGHLIGHT
                          </span>
                        )}
                      </span>
                      {(() => {
                        const chips = getPageLineChips(note);
                        return chips.length > 0 ? (
                          <span className="real-time__comments-page-line-chips">
                            {chips.map((label) => (
                              <span key={label} className="real-time__comments-page-line-chip">
                                {label}
                              </span>
                            ))}
                          </span>
                        ) : null;
                      })()}
                      <div className="real-time__comments-selection-list">
                        {visibleSelectionEntries.map((entry) => (
                          <p key={entry.id} className="real-time__comments-selection-text">
                            {entry.text}
                          </p>
                        ))}
                      </div>
                      {canExpandSelections && (
                        <button
                          type="button"
                          className="real-time__comments-section-toggle real-time__comments-section-toggle--bottom"
                          aria-expanded={isSelectionExpanded}
                          aria-label={
                            isSelectionExpanded
                              ? T.translate('case.realTimeHideSelectedText', {
                                  defaultValue: 'Collapse selected text',
                                })
                              : T.translate('case.realTimeShowSelectedText', {
                                  defaultValue: `Show ${hiddenSelectionCount} more selected text`,
                                })
                          }
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleExpandedSelection(note.highlightId);
                          }}
                        >
                          <span className="real-time__comments-section-toggle-label">
                            {isSelectionExpanded
                              ? T.translate('case.realTimeShowLess', {
                                  defaultValue: 'Show less',
                                })
                              : T.translate('case.realTimeShowMore', {
                                  defaultValue: formatHiddenCountLabel(
                                    hiddenSelectionCount,
                                    'line',
                                    'lines',
                                  ),
                                })}
                          </span>
                          {isSelectionExpanded ? (
                            <KeyboardArrowUpIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowDownIcon fontSize="small" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {commentEntries.length > 0 && (
                  <div className="real-time__comments-section">
                    <div className="real-time__comments-section-card">
                      <span className="real-time__comments-section-title">
                        {T.translate('case.realTimeComments', { defaultValue: 'Comments' })}
                      </span>
                      <div className="real-time__comments-comment-list">
                        {visibleCommentEntries.map((entry) => (
                          <div key={entry.id} className="real-time__note-comment">
                            {entry.authorName && (
                              <span className="real-time__note-comment-author">
                                {entry.authorName}
                              </span>
                            )}
                            <p className="real-time__note-comment-text">{entry.text}</p>
                            <span className="real-time__note-comment-time">
                              {formatNoteTimestamp(entry.timestamp)}
                            </span>
                          </div>
                        ))}
                      </div>
                      {canExpandComments && (
                        <button
                          type="button"
                          className="real-time__comments-section-toggle real-time__comments-section-toggle--bottom"
                          aria-expanded={isCommentsExpanded}
                          aria-label={
                            isCommentsExpanded
                              ? T.translate('case.realTimeHideComments', {
                                  defaultValue: 'Collapse comments',
                                })
                              : T.translate('case.realTimeShowComments', {
                                  defaultValue: `Show ${hiddenCommentCount} more comments`,
                                })
                          }
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleExpandedComments(note.highlightId);
                          }}
                        >
                          <span className="real-time__comments-section-toggle-label">
                            {isCommentsExpanded
                              ? T.translate('case.realTimeShowLess', {
                                  defaultValue: 'Show less',
                                })
                              : T.translate('case.realTimeShowMore', {
                                  defaultValue: formatHiddenCountLabel(
                                    hiddenCommentCount,
                                    'comment',
                                    'comments',
                                  ),
                                })}
                          </span>
                          {isCommentsExpanded ? (
                            <KeyboardArrowUpIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowDownIcon fontSize="small" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {onSubmitComment &&
                  (!canExpandComments || isCommentsExpanded) &&
                  !replyOpenIds.has(note.highlightId) && (
                    <button
                      type="button"
                      className="real-time__comments-reply-btn"
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleReplyOpen(note.highlightId);
                      }}
                    >
                      Reply
                    </button>
                  )}
                {onSubmitComment && replyOpenIds.has(note.highlightId) && (
                  <div
                    className="real-time__note-input-wrapper"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    role="presentation"
                  >
                    <textarea
                      className="real-time__note-input real-time__note-reply-input"
                      placeholder="Reply..."
                      value={replyTexts[note.highlightId] ?? ''}
                      onChange={(e) => handleReplyTextChange(note.highlightId, e.target.value)}
                      maxLength={2000}
                      disabled={submittingReplyIds.has(note.highlightId)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSubmitReply(note.highlightId);
                        }
                      }}
                      rows={2}
                      autoFocus
                    />
                    {(replyTexts[note.highlightId] ?? '').trim().length > 0 &&
                      (submittingReplyIds.has(note.highlightId) ? (
                        <span className="real-time__note-send">
                          <CircularProgress size={16} thickness={5} />
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="real-time__note-send"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSubmitReply(note.highlightId);
                          }}
                        >
                          <SendRounded style={{ fontSize: 16 }} />
                        </button>
                      ))}
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default React.memo(CommentsPanel);
